import { Icon } from "@iconify/react";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { DynamicIsland, DynamicIslandView } from "@/components/motion/dynamic-island";
import { Button } from "@/components/ui/button";
import { type TopbarIslandState, useTopbarIsland } from "@/features/workbench/topbar-island-context";
import { useI18n } from "@/locales/i18n-provider";
import { TopbarIslandOperationView } from "@/surfaces/standalone/components/topbar-island-operation-view";
import { cn } from "@/utils";

const CompactView = ({ onOpenSearch }: { onOpenSearch: () => void }) => {
  const { t } = useI18n();

  return (
    <button
      tabIndex={-1}
      aria-label={t("shell.openSearchAndCommands")}
      className="flex items-center gap-x-2 h-9.5 w-80 -my-1.5 -mx-4 px-3 justify-start rounded-none p-0 text-muted-foreground hover:bg-transparent hover:text-muted-foreground focus-visible:ring-inset"
      type="button"
      onClick={onOpenSearch}
    >
      <Icon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" icon="tabler:search" />
      <span className="whitespace-nowrap text-xs text-muted-foreground">{t("shell.searchAndCommands")}</span>
      <kbd
        aria-hidden
        className="ml-auto inline-flex items-center rounded-sm border bg-muted px-1.5 py-0.5 font-mono text-xs font-normal leading-none text-muted-foreground"
      >
        ⌘ K
      </kbd>
    </button>
  );
};

const TaskView = ({ state }: { state: TopbarIslandState | null }) => {
  const { t } = useI18n();
  const { dismiss } = useTopbarIsland();
  const { view, data } = state || {};

  if (view !== "task") return null;

  const taskProgress = data?.progress !== undefined ? Math.min(100, Math.max(0, data.progress)) : undefined;

  return (
    <div className="flex w-80 items-center gap-3 px-4 py-3">
      <span className="grid size-8 shrink-0 place-items-center rounded-lg border border-warning/20 bg-warning/10 text-warning">
        <Icon aria-hidden className="size-4 animate-spin" icon="tabler:loader-2" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3">
          <span className="truncate text-sm font-semibold leading-5 text-foreground">{data?.label}</span>
          {taskProgress !== undefined ? <span className="ml-auto text-xs tabular-nums text-muted-foreground">{Math.round(taskProgress)}%</span> : null}
        </div>
        <div
          aria-label={taskProgress === undefined ? t("shell.taskRunning") : t("shell.taskProgress", { progress: Math.round(taskProgress) })}
          aria-valuemax={100}
          aria-valuemin={0}
          aria-valuenow={taskProgress}
          className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted"
          role="progressbar"
        >
          <div
            className={cn("h-full rounded-full bg-warning", taskProgress === undefined && "animate-pulse")}
            style={{ width: taskProgress === undefined ? "35%" : `${taskProgress}%` }}
          />
        </div>
      </div>

      <Button aria-label={t("shell.dismissTask")} size="icon-xs" variant="ghost" onClick={() => dismiss(data?.id)}>
        <Icon aria-hidden icon="tabler:x" />
      </Button>
    </div>
  );
};

const NotificationView = ({ state }: { state: TopbarIslandState | null }) => {
  const { t } = useI18n();
  const { dismiss } = useTopbarIsland();
  const { view, data } = state ?? {};

  if (view !== "notification") return null;

  const notificationCount = data?.unreadCount;

  return (
    <div className="flex w-72 items-center gap-3 px-4 py-3">
      <span className="grid size-8 shrink-0 place-items-center">
        <Icon aria-hidden className="size-5 text-primary" icon="line-md:bell-twotone-loop" />
      </span>

      <Link className="min-w-0 flex-1 text-left" to="/notifications" onClick={() => dismiss()}>
        <span className="block text-sm font-semibold leading-5 text-foreground">{t("notifications.new")}</span>
        <span className="block truncate text-xs leading-4 text-muted-foreground">
          {notificationCount ? t("notifications.unreadShort", { count: notificationCount }) : t("notifications.openCenter")}
        </span>
      </Link>

      <Button aria-label={t("notifications.dismissPreview")} size="icon-xs" variant="ghost" onClick={() => dismiss()}>
        <Icon aria-hidden icon="tabler:x" />
      </Button>
    </div>
  );
};

const VIEW_CLASSNAMES: Record<string, string> = {
  notification: "border-primary/20 shadow-lg w-auto!",
  operation: "border-primary/20 shadow-md",
  task: "border-warning/20 shadow-md",
};

/** 只负责把顶栏状态映射为灵动岛视图，不读取任何业务数据。 */
export function TopbarDynamicIsland({ onOpenSearch }: { onOpenSearch: () => void }) {
  const { setHostAvailable, state } = useTopbarIsland();
  const { view } = state ?? {};

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1280px)");
    const updateAvailability = () => setHostAvailable(mediaQuery.matches);
    updateAvailability();
    mediaQuery.addEventListener("change", updateAvailability);
    return () => {
      mediaQuery.removeEventListener("change", updateAvailability);
      setHostAvailable(false);
    };
  }, [setHostAvailable]);

  return (
    <DynamicIsland
      className={cn(
        "pointer-events-auto min-h-10 min-w-70 border bg-card shadow-none text-foreground transition-colors hover:border-foreground/20",
        view && VIEW_CLASSNAMES[view],
      )}
      compact={<CompactView onOpenSearch={onOpenSearch} />}
      view={view ?? null}
    >
      <DynamicIslandView className="items-start justify-start p-0" id="notification">
        <NotificationView state={state} />
      </DynamicIslandView>
      <DynamicIslandView className="items-start justify-start p-0" id="task">
        <TaskView state={state} />
      </DynamicIslandView>

      <DynamicIslandView className="items-start justify-start p-0" id="operation">
        <TopbarIslandOperationView operation={state?.view === "operation" ? state.data : null} />
      </DynamicIslandView>
    </DynamicIsland>
  );
}
